import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';

import { BrandLogo } from '@/shared/ui/BrandLogo';
import { Icon } from '@/shared/ui/Icon';
import type { IconName } from '@/shared/ui/Icon';

const PROMISES: ReadonlyArray<{ icon: IconName; key: 'web:auth.promise.verified' | 'web:auth.promise.direct' | 'web:auth.promise.local' }> = [
  { icon: 'verified', key: 'web:auth.promise.verified' },
  { icon: 'broadcast', key: 'web:auth.promise.direct' },
  { icon: 'pin', key: 'web:auth.promise.local' },
];

/**
 * L'espace public : les écrans d'entrée (bienvenue, connexion, inscription,
 * code SMS). Sur ordinateur, la marque occupe la moitié gauche, dans l'encre de
 * marque, le seul fond coloré de toute l'application (comme l'écran de
 * lancement du mobile) ; le formulaire vit seul à droite. Sur téléphone, le
 * formulaire seul.
 */
export function AuthLayout() {
  const { t } = useTranslation(['common', 'web', 'account']);
  return (
    <div className="grid min-h-dvh bg-bg lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <aside
        data-theme="dark"
        className="relative hidden overflow-hidden bg-brand-ink px-12 py-12 text-white lg:flex lg:flex-col"
      >
        <BrandLogo size={44} wordmark className="[&_span]:text-white" />
        <div className="mt-auto max-w-[460px]">
          <p className="text-hero text-white">{t('web:auth.headline')}</p>
          <p className="mt-4 text-body text-white/70">{t('web:auth.subline')}</p>
          <ul className="mt-10 flex flex-col gap-5">
            {PROMISES.map((promise) => (
              <li key={promise.key} className="flex items-start gap-3.5">
                <span className="grid size-10 shrink-0 place-items-center rounded-pill border border-white/15 bg-white/5">
                  <Icon name={promise.icon} size={20} className={promise.icon === 'verified' ? 'text-verified' : 'text-white'} />
                </span>
                <span className="pt-2 text-subtle text-white/85">{t(promise.key)}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-12 text-caption text-white/45">© {new Date().getFullYear()} BUL · Business Link</p>
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 size-[420px] rounded-pill border border-white/5"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -top-8 -right-8 size-[260px] rounded-pill border border-verified/20"
        />
      </aside>
      <div className="flex min-h-dvh flex-col">
        <div className="flex flex-1 items-start justify-center px-4 py-8 sm:items-center sm:py-12">
          <div className="w-full max-w-[420px]">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
